import { Box } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'
import { useReleaseChannel } from '@/features/auth'

import type { GuidanceCheckPoint, RelatedKasanType } from '../../types'
import { InlineTag } from '../GuidancePrepCard/parts'
import { GuidancePrepRow } from '../GuidancePrepRow'

interface Props {
  point: GuidanceCheckPoint
  // false で禁忌 marker を出さない (= 常に ・). 既定 true.
  // β の指導前準備のカードの服薬指導の面が false を渡す (● は監査の面に寄せる.
  // yakureki-back log/262 work-log/08 §5).
  showsContraindication?: boolean
  // true で患者への聞き方 (question) を論拠の次の行に出す. 既定 false.
  // 出すのは β の指導前準備のカードの服薬指導の面だけ (監査の面・ビデオ通話のパネルは出さない.
  // yakureki-back log/262 決定 14).
  showsQuestion?: boolean
}

// 関連加算 inline chip 表示用 label. 「加算」 suffix は加算 panel と同じ canonical name で維持.
const KASAN_LABELS: Record<RelatedKasanType, string> = {
  high_risk: 'ハイリスク薬加算',
  infant: '乳幼児服薬指導加算',
  rmp: 'RMP加算',
  inhalation: '吸入薬指導加算',
  adjustment_support: '服用薬剤調整支援料',
  sentei: '選定療養',
}

// marker (・ / ●) + 主 (statement [+ 関連加算 chip]) / 従 (論拠 [+ 患者への聞き方]) の行. 行構造は GuidancePrepRow に集約.
// 論拠 (context) は statement 直下に従属させ、主従を字色で明示する (加算行と同 spec). 括弧で 1 行に詰めない.
//
// 禁忌該当 (is_contraindicated) は **marker だけ** で出す:
//   通常   ・ / fg.faint
//   禁忌   ● / state.dangerFg (#C4554D)
// marker は span で描く**文字**なので、アイコン用の dangerIcon (#D44C47) ではなく
// 「danger 文字」の dangerFg を取る. Stop ボタンのラベルと同色で、赤の中では柔らかい側.
//
// **design-book 00-principles/04-color-discipline.md §3 「重要度を色で出さない」からの逸脱**
// (2026-09-02 松田さん決定. 逸脱の登録は hub/design-book 04-apps/kakeru.md).
// 禁忌を「エラーと同じ扱い」に置く判断で、既存 token (state.dangerFg) の流用に留め
// 新色は足していない. §5 「エラー・警告の専用色を増やさない」は守っている.
//
// 逸脱していないもの:
//   - 背景色・枠は足さない (03-patterns/realtime-guidance.md 判定基準)
//   - 色相だけに頼らず marker 字形も変える (・ → ●. status-dot の塗り/輪郭二値と同趣旨)
//   - statement / context の文面は変えない (「[禁忌]」を本文へ焼き込まない)
//
// **出口は先行版 (β) 店舗に閉じる** (release_channel >= beta).
// back は全店に is_contraindicated を返すが、赤 marker は色規律からの逸脱なので
// 全店へ一度に出さず、先行版で運用を見てから ga へ開く。開くときは isBeta の
// 1 語を消す diff で済む (フラグも設定項目も足していない)。
// 確認要点を描く面は 2 つある (GuidancePrepPaneV3 / VideoCallGuidancePanel) が、
// いずれも GuidanceCheckPointList 経由で本 component に入るので gate はここ 1 箇所で足りる。
// (録音後の画面の再掲は hub log/108 決定 44 で消した)
export const GuidanceCheckPointCard = ({
  point,
  showsContraindication: allowsContraindication = true,
  showsQuestion = false,
}: Props) => {
  const { isBeta } = useReleaseChannel()
  // 「禁忌該当」かつ「先行版店舗」のときだけ marker を ● / danger にする.
  const showsContraindication =
    allowsContraindication && isBeta && point.is_contraindicated
  const question = showsQuestion ? point.question : undefined
  const kasanLabel = point.related_kasan_type
    ? KASAN_LABELS[point.related_kasan_type]
    : null
  return (
    <GuidancePrepRow
      marker={showsContraindication ? '●' : '・'}
      markerColor={
        showsContraindication
          ? colorTokens.state.dangerFg
          : colorTokens.fg.faint
      }
      main={
        <BodyText>
          {point.statement}
          {kasanLabel && <InlineTag>{kasanLabel}</InlineTag>}
        </BodyText>
      }
      sub={
        point.context || question ? (
          <>
            {point.context}
            {/* 患者への聞き方. 論拠の次の行に「」で括り、 そのまま口にできる 1 文と分かるようにする */}
            {question && (
              <Box component="span" sx={{ display: 'block' }}>
                「{question}」
              </Box>
            )}
          </>
        ) : null
      }
    />
  )
}
